import { useRef, useState } from "react";
import { generateSignedUrl } from "../../api/userService";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Modal,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";

const SupportForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    issue_type: "",
    issue_category: "",
    details: "",
    attachment: null,
  });

  const fileInputRef = useRef(null); //Create a ref for the file input
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const issueCategories = [
    "UX/UI",
    "Ingestion",
    "Extraction",
    "Decision Grid",
    "Decision Management",
    "Communicator",
    "Bots",
    "Other",
  ];

  const validateForm = () => {
    let newErrors = {};
    if (!formData.customer_name.trim() || formData.customer_name.length < 3) {
      newErrors.customer_name = "Customer name must be at least 3 characters.";
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email.trim() || !emailPattern.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
    }

    if (!formData.issue_type) {
      newErrors.issue_type = "Please select an issue type.";
    }

    if (!formData.issue_category) {
      newErrors.issue_category = "Please select an issue category.";
    }

    if (!formData.details.trim() || formData.details.length < 10) {
      newErrors.details = "Details must be at least 10 characters.";
    }

    if (formData.attachment) {
      const allowedFormats = ["png", "jpg", "jpeg", "pdf", "doc", "docx"];
      const fileExtension = formData.attachment.name
        .split(".")
        .pop()
        .toLowerCase();
      if (!allowedFormats.includes(fileExtension)) {
        newErrors.attachment =
          "Invalid file type. Only images, PDFs, or Word docs are allowed.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, attachment: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      setModalMessage("Submitting your request...");

      try {
        // Simulate an API call
        await generateSignedUrl(formData);
        setLoading(false);
        setModalMessage(
          "Your support request has been submitted successfully!"
        );
        // Reset form after successful submission
        setFormData({
          customer_name: "",
          email: "",
          issue_type: "",
          issue_category: "",
          details: "",
          attachment: null,
        });
        setErrors({}); // Clear validation errors
        if (fileInputRef.current) {
          // Reset the file input manually
          fileInputRef.current.value = "";
        }
      } catch (error) {
        setLoading(false);
        setModalMessage("Submission failed. Please try again later.");
        console.error("Form submission failed", error);
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        onClose();
        setModalMessage("");
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "75%", md: "60%", lg: "40%" },
          maxWidth: "500px",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: "8px",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", color: "#FF4500", mb: 2 }}
        >
          Support Form
        </Typography>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <TextField
            label="Customer Name"
            name="customer_name"
            value={formData.customer_name}
            onChange={handleChange}
            fullWidth
            error={!!errors.customer_name}
            helperText={errors.customer_name}
          />

          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            error={!!errors.email}
            helperText={errors.email}
          />

          <FormControl fullWidth error={!!errors.issue_type}>
            <InputLabel>Bug or Product Enhancement</InputLabel>
            <Select
              label="Bug or Product Enhancement"
              name="issue_type"
              value={formData.issue_type}
              onChange={handleChange}
            >
              <MenuItem value="Bug">Bug</MenuItem>
              <MenuItem value="Enhancement">Product Enhancement</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth error={!!errors.issue_category}>
            <InputLabel>List of Possible Issues</InputLabel>
            <Select
              label="List of Possible Issues"
              name="issue_category"
              value={formData.issue_category}
              onChange={handleChange}
            >
              {issueCategories.map((category, index) => (
                <MenuItem key={index} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Details"
            name="details"
            value={formData.details}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            error={!!errors.details}
            helperText={errors.details}
          />

          <div>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Attachment (Image, PDF, Word)
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
              onChange={handleFileChange}
            />
            {errors.attachment && (
              <Typography color="error" variant="body2">
                {errors.attachment}
              </Typography>
            )}
          </div>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ bgcolor: "#FF4500", color: "white", mt: 2 }}
          >
            Submit
          </Button>
        </form>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 2,
            }}
          >
            <CircularProgress sx={{ color: "#FF4500" }} />
            <Typography sx={{ mt: 2 }}>{modalMessage}</Typography>
          </Box>
        ) : (
          modalMessage && (
            <Typography
              sx={{
                mt: 2,
                color: modalMessage.includes("failed")
                  ? "error.main"
                  : "success.main",
              }}
            >
              {modalMessage}
            </Typography>
          )
        )}
      </Box>
    </Modal>
  );
};

export default SupportForm;
